import { FC, useState, useEffect, ChangeEvent } from 'react';
import {
  Divider,
  Box,
  Card,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableContainer,
  Typography,
  Grid,
  Button,
  TablePagination,
  Tooltip,
  TextField,
  InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import styled from '@emotion/styled';
import { TableHeaderCell } from 'src/components/TableHeaderCell';
import { withStyles } from '@mui/styles';
import PageTitleWrapper from 'src/components/PageTitleWrapper';
import AddTwoToneIcon from '@mui/icons-material/AddTwoTone';
import RoleAddForm from './RoleAddForm';
import { RoleRequest } from 'src/models/roles';
import { AppActions } from 'src/store/AppReducer';
import { useDispatch } from 'react-redux';
import RoleEditForm from './RoleEditForm';
import { useNavigate } from 'react-router';
import { deleteRole, getAllRoles, searchRole } from 'src/services/RoleManagementService';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { TableColumnCell } from 'src/components/TableColumnCell';
import moment from 'moment';
import ConfirmationModal from '../common/confirmationModal/ConfirmationModal';
import SnackBar from 'src/components/SnackBar/SnackBar';
import { useAppSelector } from 'src/hooks/hooks';

interface RoleTableProps {}

interface Filters {
  type: string;
  po_status: string;
  status: string;
  cat: string;
}

const applyFilters = (roles: any[], filters: Filters): any[] => {
  return roles.filter((rolesStatus) => {
    let matches = true;
    // if (filters.status && employeeStatus.status !== filters.status) {
    //   matches = false;
    // }
    return matches;
  });
};

const applyPagination = (
  roleList: RoleRequest[],
  page: number,
  limit: number
): RoleRequest[] => {
  return roleList.slice(page * limit, page * limit + limit);
};

const RoleTable: FC<RoleTableProps> = () => {
  const [filters, setFilters] = useState<Filters>({
    type: null,
    po_status: null,
    status: null,
    cat: null
  });
  const [roles, setRoles] = useState([] as RoleRequest[]);
  const { isLoading } = useAppSelector((state) => state.appReducer);
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRoleId, setDeleteRoleId] = useState('');
  const [editOpen, setEditOpen] = useState(false);

  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('');
  const [snackBarMessage, setSnackBarMessage] = useState('');

  const [role, setRole] = useState({} as RoleRequest);

  const [page, setPage] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);

  const [editData, setEditData] = useState({});
  const [visibility, setVisibility] = useState(false);

  const [currentUser, setCurrentUser] = useState<any>({});

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setCurrentUser(user);
  }, []);

  const getAllUserRoles = () => {
    dispatch(AppActions.showLoader());

    getAllRoles().then((res) => {
      const sorted = res?.data.sort((a, b) => {
        var dateA = moment.utc(a.createdDate).format();
        var dateB = moment.utc(b.createdDate).format();

        // Turn your strings into dates, and then subtract them
        // to get a value that is either negative, positive, or zero.

        return Date.parse(dateB) - Date.parse(dateA);
      });

      setRoles(sorted as RoleRequest[]);
      dispatch(AppActions.hideLoader());
    });
  };

  useEffect(() => {
    getAllUserRoles();
  }, []);

  const handlePageChange = (event: any, newPage: number): void => {
    setPage(newPage);
  };

  const handleLimitChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setLimit(parseInt(event.target.value));
  };

  const filteredRoles = applyFilters(roles, filters);

  const paginatedRoles = applyPagination(filteredRoles, page, limit);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = (role: RoleRequest) => {
    if (role !== null && role !== undefined)
      setRoles((prev) => [...prev, role]);
    setOpen(false);
  };

  const handleEditClickOpen = () => {
    setEditOpen(true);
  };

  const handleEditClose = (role: RoleRequest) => {
    setRole({} as RoleRequest);
    setEditOpen(false);
  };

  const toggleDelete = () => {
    setDeleteOpen(!deleteOpen);
  };

  const onDeleteConfirm = () => {
    deleteRole(deleteRoleId).then(() => {
      setSnackBarMessage('You’ve deleted the user role successfully.');
      setSnackbarSeverity('success');
      setSnackBarOpen(true);
      getAllUserRoles();
      toggleDelete();
    });
  };

  // custom table row with different color
  const CustomTableRow = withStyles({
    root: {
      background: '#E6E9FF !important',
      height: '3.5rem'
    }
  })(TableRow);

  const [searchKeywords, setSearchKeywords] = useState('');

  const nameChange = (event) => {
    setSearchKeywords(event.target.value);
  };

  const searchGroupsAll = async (keyword: string) => {
    dispatch(AppActions.showLoader());
    const result = await searchRole(keyword);
    setRoles(result?.data);
    dispatch(AppActions.hideLoader());
  };

  useEffect(() => {
    setPage(0);
    if (searchKeywords.length > 0) {
      searchGroupsAll(searchKeywords);
    }
    if (searchKeywords.length === 0) {
      getAllUserRoles();
    }
  }, [searchKeywords]);

  return (
    <>
      <Grid container sx={{ mt: 2, mb: 2 }} justifyContent="space-between">
        <Grid item>
          <Typography
            style={{
              marginLeft: '0px',
              marginTop: '7px',
              fontWeight: '700',
              fontSize: '16px',
              fontFamily:
                'Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol"'
            }}
          >
            Role Management
          </Typography>
          <Typography variant="subtitle2"></Typography>
        </Grid>
        <Grid item>
          <Button
            sx={{
              mt: { xs: 2, md: 0 },
              fontSize: '11px',
              fontWeight: 'normal'
            }}
            variant="contained"
            startIcon={<AddTwoToneIcon fontSize="small" />}
            onClick={handleClickOpen}
            style={{ height: '1.75rem' }}
          >
            Create New
          </Button>
          <RoleAddForm
            open={open}
            onClose={handleClose}
            getData={getAllUserRoles}
          />
        </Grid>
      </Grid>
      <Card>
        <Grid
          container
          style={{
            justifyContent: 'space-between',
            padding: '1rem'
          }}
          spacing={2}
        >
          <Grid item xs={6}>
            <TextField
              sx={{ width: 320, mr: 2 }}
              size="small"
              placeholder="Role Name"
              onChange={(e) => nameChange(e)}
              InputProps={{
                style: { fontSize: '12px', height: '1.75rem' },
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ color: '#d3d3d3' }}
                      style={{ fontSize: '15px' }}
                    />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
        </Grid>
        {/* <CardHeaderWrapper action={<></>} title="" /> */}
        <Divider />
        <TableContainer>
          <Table style={{ borderCollapse: 'separate' }} size="small">
            <TableHead>
              <CustomTableRow>
                <TableHeaderCell content="Role Name" />
                <TableHeaderCell content="Accessible tabs" />
                <TableHeaderCell content="Start Date" />
                <TableHeaderCell content="End Date" />
                <TableHeaderCell content="Actions" sticky />
              </CustomTableRow>
            </TableHead>

            <TableBody>
              {(paginatedRoles || []).map((item, i) => {
                const correctOrder = [
                  'Dashboard',
                  'Employee',
                  'Skills',
                  'Project',
                  'Project Health Summary',
                  'Segments',
                  'Resource Management',
                  'Leave Management',
                  'Work Model',
                  'Activity Management',
                  'Admin',
                  'Year Plan'
                ];
                let sorted = [];
                correctOrder.forEach((k) => {
                  let n = item.tabs.filter((obj) => {
                    let availablePermissions: any = [];

                    let perms: any = [];
                    let fields: any = [];
                    if (obj?.permissions?.length > 0) {
                      perms = obj?.permissions?.filter((perm) => perm.value);
                    }

                    if (obj?.fields?.length > 0) {
                      fields = obj?.fields?.filter(
                        (perm) => perm.view || perm.edit
                      );
                    }

                    availablePermissions = [...perms, ...fields];

                    if (availablePermissions?.length > 0) {
                      return obj.name === k;
                    } else {
                      return null;
                    }
                  });
                  if (n?.length > 0) {
                    sorted.push(n[0]);
                  }
                });

                sorted = sorted.map((sort) => {
                  if (sort.name === 'Employee') {
                    return {
                      ...sort,
                      name: 'Employees'
                    };
                  } else if (sort.name === 'Project') {
                    return {
                      ...sort,
                      name: 'Projects'
                    };
                  } else if (sort.name === 'Admin') {
                    return {
                      ...sort,
                      name: 'Admin Configurations'
                    };
                  }else if (sort.name === 'Skills') {
                    return {
                      ...sort,
                      name: 'Holiday Calender'
                    };
                  }else if (sort.name === 'Work Model' || sort.name === 'Work From Home') {
                    return {
                      ...sort,
                      name: 'Leave Management'
                    };
                  }else if (sort.name === 'Activity Management') {
                    return {
                      ...sort,
                      name: 'Timesheet'
                    };
                  } else {
                    return sort;
                  }
                });
                return (
                  <TableRow hover sx={{ height: '80%' }} key={i}>
                    <TableColumnCell>
                      <Typography
                        style={{ fontSize: '11px' }}
                        variant="body1"
                        color="text.primary"
                        gutterBottom
                        noWrap
                        onClick={() => {}}
                      >
                        {item.name}
                      </Typography>
                    </TableColumnCell>
                    <TableColumnCell>
                      <Typography
                        variant="body1"
                        color="text.primary"
                        style={{ fontSize: '11px', display: 'flex' }}
                        gutterBottom
                        noWrap
                      >
                        <Typography
                          variant="body1"
                          style={{
                            fontSize: '11px',
                            marginBottom: '1rem'
                          }}
                          color="text.primary"
                          gutterBottom
                          noWrap
                        >
                          {sorted.map(e => e.name).reduce((eccumulator, item) => {
                            if(!eccumulator.includes(item)){
                              eccumulator.push(item);
                            }
                            return eccumulator;
                          }, []).join(', ')}
                          &nbsp;
                        </Typography>
                      </Typography>
                    </TableColumnCell>
                    <TableColumnCell>
                      <Typography
                        variant="body1"
                        color="text.primary"
                        style={{ fontSize: '11px', display: 'flex' }}
                        gutterBottom
                        noWrap
                      >
                        {moment(item.startDate).format('DD/MM/YYYY')}
                      </Typography>
                    </TableColumnCell>
                    <TableColumnCell>
                      <Typography
                        variant="body1"
                        color="text.primary"
                        style={{ fontSize: '11px', display: 'flex' }}
                        gutterBottom
                        noWrap
                      >
                        {item.endDate
                          ? moment(item.endDate).format('DD/MM/YYYY')
                          : '-'}
                      </Typography>
                    </TableColumnCell>
                    <TableColumnCell sticky>
                      <div
                        style={{
                          background: 'white',
                          marginLeft: 'auto',
                          display: 'flex',
                          justifyContent: 'center'
                        }}
                      >
                        {item.name !== 'Super User' &&
                          currentUser.Roles !== 'General User' && (
                          <Tooltip title="Edit" placement="top" >
                            <EditIcon
                              onClick={() => {
                                handleEditClickOpen();
                                setEditData(item);
                                setVisibility(false);
                              }}
                              style={{
                                fontSize: '17px',
                                cursor: 'pointer'
                              }}
                            />
                          </Tooltip>
                          )}
                        <Tooltip title="View" placement="top" >
                        <VisibilityIcon
                          onClick={() => {
                            handleEditClickOpen();
                            setEditData(item);
                            setVisibility(true);
                          }}
                          style={{
                            fontSize: '17px',
                            cursor: 'pointer'
                          }}
                        />
                        </Tooltip>
                        {item.name !== 'Super User' &&
                          currentUser.Roles.split(',').includes('Super User') && (
                            <Tooltip title="Delete" placement="top" >
                              <DeleteOutlineIcon
                              onClick={() => {
                                setDeleteRoleId(item.roleId);
                                toggleDelete();
                              }}
                              style={{
                                fontSize: '17px',
                                cursor: 'pointer'
                              }}
                            />
                            </Tooltip> 
                          )}
                      </div>
                    </TableColumnCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {paginatedRoles?.length === 0 && (
          <Typography
            style={{
              fontSize: '15px',
              fontWeight: 'bold',
              margin: '1rem',
              textAlign: 'center'
            }}
            variant="body2"
          >
            No Records Found
          </Typography>
        )}
        <Box p={2}>
          <TablePagination
            sx={{
              fontSize: '12px',
              '.MuiTablePagination-displayedRows': {
                fontSize: '12px'
              },
              '.MuiTablePagination-selectLabel': {
                fontSize: '12px'
              }
            }}
            component="div"
            count={filteredRoles?.length}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleLimitChange}
            page={page}
            rowsPerPage={limit}
            rowsPerPageOptions={[10, 20, 30, 40, 50]}
          />
        </Box>
        <RoleEditForm
          open={editOpen}
          onClose={handleEditClose}
          role={role}
          setRoles={setRoles}
          roles={roles}
          data={editData}
          getData={getAllUserRoles}
          visibility={visibility}
        />
        <ConfirmationModal
          isLoading={isLoading}
          open={deleteOpen}
          toggle={toggleDelete}
          message={`Are you sure you want to delete this role?`}
          onConfirm={() => onDeleteConfirm()}
          noToggle={toggleDelete}
        />
        <SnackBar
          snackBarOpen={snackBarOpen}
          setSnackBarOpen={setSnackBarOpen}
          snackBarMessage={snackBarMessage}
          snackbarSeverity={snackbarSeverity}
        />
      </Card>
    </>
  );
};

RoleTable.propTypes = {};

RoleTable.defaultProps = {};

export default RoleTable;
